import { toJS } from 'mobx';
import React, { forwardRef, useEffect, useRef } from 'react';
import { useTable, usePagination, useSortBy, useRowSelect, useBlockLayout } from 'react-table';
import { PAGE_SIZE_OPTIONS, TABLE_CODE, WIDTH_COLUMN_SELECT_TABLE } from '../../core/configs/constants';
import PagingInfo from './PagingInfo';
import ReactPaging from './ReactPaging';
import SelectPageSize from './SelectPageSize';
import './style.scss';
import classNames from 'classnames';

const IndeterminateCheckbox = forwardRef(({ indeterminate, ...rest }, ref) => {
    const defaultRef = useRef();
    const resolvedRef = ref || defaultRef;

    useEffect(() => {
        resolvedRef.current.indeterminate = indeterminate;
    }, [resolvedRef, indeterminate]);

    return (
        <>
            <input type='checkbox' ref={resolvedRef} {...rest} style={{ 'width': '16px', 'height': '16px' }} />
        </>
    );
});

const isOrderByStateChange = (newState, oldState) => {
    const newSort = newState.sortBy || [];
    const oldSort = oldState?.sortBy || [];

    if (newSort.length !== oldSort.length) return true;

    for (let i = 0; i < newSort.length; i++) {
        if (newSort[i].id !== oldSort[i].id || newSort[i].desc !== oldSort[i].desc) {
            return true;
        }
    }
    return false;
};

const ReactTable = ({
    tblCode,
    columns,
    data,
    enableSelectRow,
    disableSelectionPageSize,
    disablePaging,
    disableField,
    disableFieldValue,
    element,
    customSelectedBox,
    pageIndexProp,
    changePageIndexProp,
    enableServerSidePaging,
    onFetch,
    rowClass,
    initialTableState,
    hideTableHeader,
    hideTableFooter,
    className,
    tableFooter,
    onSelectedChange,
    disabledNoData,
    isSelectedAll,
    selectedRowIds,
    disableSubRowSelect,
    lstDisabledRowSelect,
    showDisabledRowSelect,
    checkedDisabledRowSelect,
    useCustomSelectedAll,
    onSelectedAllChange,
    isSelectedAllCustom,
    rowKey,
    getRowId,
    showPagingInfo,
    scrollHorizontalWidth,
    freezeColumn,
    disableMultiSort = false,
    isMultiSortEvent = undefined,
    enableDragScroll = false,
    enableStickyHeader = false
}) => {

    const lstDisabledRowSelectLength = lstDisabledRowSelect?.filter(e => e)?.length;

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        rows,
        pageOptions,
        gotoPage,
        setPageSize,
        selectedFlatRows,
        toggleAllRowsSelected,
        setSortBy,
        state
    } = useTable(
        {
            columns,
            data,
            initialState: { initialTableState, selectedRowIds },
            manualSortBy: enableServerSidePaging,

            disableMultiSort,
            isMultiSortEvent,

            getRowId: getRowId || ((row, relativeIndex, parent) => {
                const rowData = toJS(row);
                const rowId = rowKey && rowData ? rowData[rowKey] : relativeIndex;
                const id = parent ? [parent.id, relativeIndex].join('.') : rowId;
                return id;
            })
        },
        useSortBy,
        usePagination,
        useRowSelect,
        (hooks) => {

            hooks.visibleColumns.push((columns) => {
                return enableSelectRow ?
                    [
                        {
                            id: 'selection',
                            width: WIDTH_COLUMN_SELECT_TABLE,
                           
                           
                            Header: (({ getToggleAllRowsSelectedProps }) => {
                                if (useCustomSelectedAll) {
                                    return (
                                        <div>
                                            <IndeterminateCheckbox
                                                checked={isSelectedAllCustom}
                                                onChange={(e) => onSelectedAllChange(e.target.checked)}
                                                disabled={lstDisabledRowSelectLength === data?.length}
                                                readOnly={lstDisabledRowSelectLength === data?.length}
                                            />
                                        </div>
                                    )
                                }
                                else if ((disableSubRowSelect || disableSubRowSelect?.length > 0) &&
                                    !lstDisabledRowSelect?.includes(false) && !checkedDisabledRowSelect) {
                                    return (
                                        <div>
                                            <IndeterminateCheckbox
                                                disabled={true}
                                                readOnly={true}
                                            />
                                        </div>
                                    )
                                } else return (
                                    <div>
                                        <IndeterminateCheckbox
                                            {...getToggleAllRowsSelectedProps()}
                                            id={'selectedAll'}
                                        />
                                    </div>
                                )
                            }),
                            Cell: (({ row }) => {
                                if ((disableSubRowSelect || disableSubRowSelect?.length > 0) &&
                                    lstDisabledRowSelect?.at(row.index)) {
                                    return (
                                        showDisabledRowSelect &&
                                        <div>
                                            <IndeterminateCheckbox
                                                disabled={true}
                                                readOnly={true}
                                                checked={checkedDisabledRowSelect}
                                            />
                                        </div>
                                    )
                                }
                                else {
                                    const rowProps = row.getToggleRowSelectedProps();
                                    return (
                                        <div>
                                            <IndeterminateCheckbox
                                                {...rowProps}
                                            />
                                        </div>
                                    )
                                }
                            })
                        },
                        ...columns
                    ] :
                    columns;
            });
        }
    );

    //old effect sort
    useEffect(() => {
        if (
            enableServerSidePaging && isOrderByStateChange(state, initialTableState)
        ) {
            onFetch(state);
        }
    }, [
        onFetch,
        state,
        initialTableState,
        enableServerSidePaging
    ]);

    useEffect(() => {
        if (!enableDragScroll) return;
        const slider = document.querySelector('.table-content');


        if (!slider) {
            console.log('Not found slider element');
            return;
        }


        slider.style.cursor = 'grab';
        slider.style.userSelect = 'none';

        let isDown = false;
        let startX;
        let scrollLeft;

        const onMouseDown = (e) => {
            isDown = true;
            slider.style.cursor = 'grabbing';
            startX = e.pageX - slider.offsetLeft;
            scrollLeft = slider.scrollLeft;
        };

        const onMouseLeave = () => {
            isDown = false;
            slider.style.cursor = 'grab';
        };

        const onMouseUp = () => {
            isDown = false;
            slider.style.cursor = 'grab';
        };

        const onMouseMove = (e) => {
            if (!isDown) return;
            const x = e.pageX - slider.offsetLeft;
            const walk = (x - startX) * 2;

            slider.scrollLeft = scrollLeft - walk;
        };


        slider.addEventListener('mousedown', onMouseDown);
        slider.addEventListener('mouseleave', onMouseLeave);
        slider.addEventListener('mouseup', onMouseUp);
        slider.addEventListener('mousemove', onMouseMove);


        return () => {
            slider.removeEventListener('mousedown', onMouseDown);
            slider.removeEventListener('mouseleave', onMouseLeave);
            slider.removeEventListener('mouseup', onMouseUp);
            slider.removeEventListener('mousemove', onMouseMove);


            slider.style.cursor = '';
            slider.style.userSelect = '';
        };
    }, []);

    useEffect(() => {
        state.selectedRowIds = selectedRowIds;
    }, [selectedRowIds])

    useEffect(() => {
        if (initialTableState) state.pageSize = Number(initialTableState.pageSize);
    }, [initialTableState]);

    useEffect(() => {
        if (enableSelectRow && onSelectedChange) {
            const rows = [];
            const tableData = toJS(data);
            Object.keys(state.selectedRowIds).forEach(idx => {
                if (rowKey && state.selectedRowIds[idx]) {
                    const row = tableData.find(item => item[rowKey] === (rowKey === 'id' ? Number(idx) : idx));
                    if (row) {
                        rows.push(row);
                    }
                }
                if (!rowKey && state.selectedRowIds[idx]) {
                    if (tableData[Number(idx)]) {
                        rows.push(tableData[Number(idx)]);
                    }
                }
            })

            onSelectedChange(state.selectedRowIds, rows)
        }
    }, [selectedFlatRows.length])

    useEffect(() => {
        if (isSelectedAll !== null) {
            toggleAllRowsSelected(isSelectedAll);
        }
    }, [isSelectedAll])


    let renderITems = disablePaging ? rows : page;
    if (data.length > 0) {
        if (pageIndexProp + 1 <= pageOptions.length) {
            if (pageIndexProp !== state.pageIndex) {
                gotoPage(pageIndexProp);
            }
        } else {
            changePageIndexProp(0);
            return '';
        }
    }
    disablePaging = disablePaging && data.length > 0;

    return (
        <div className={'react-table ' + className + (enableStickyHeader ? ' enable-sticky-header' : '')}>
            {!disablePaging && (
                <div className='selection-header' style={{ display: disableSelectionPageSize ? 'none' : '' }}>
                    <SelectPageSize
                        value={state.pageSize}
                        onChange={(e) => {
                            if (enableServerSidePaging) {
                                state.pageIndex = 0;
                                state.pageSize = Number(e.target.value);
                                onFetch(state);
                            } else {
                                changePageIndexProp(0);
                                setPageSize(Number(e.target.value));
                            }
                        }}
                        values={customSelectedBox ? customSelectedBox : PAGE_SIZE_OPTIONS}
                    />
                    {element}
                </div>
            )}
            <div className={classNames(freezeColumn && 'table-content overflow-auto mb-4')} style={{ 'maxWidth': '100%', 'overflow': 'overlay' }}>
                <table
                    {...getTableProps()}
                    className={classNames('table table-bordered', freezeColumn && 'table-with-freeze-column')}
                    style={{ 'minWidth': scrollHorizontalWidth || '100%' }}
                >
                    {!hideTableHeader &&
                        <thead>
                            {headerGroups.map((headerGroup, i) => (
                                <tr {...headerGroup.getHeaderGroupProps()} key={i}>
                                    {headerGroup.headers.map((column, k) => (
                                        <th
                                            {...column.getHeaderProps(
                                                column.canSort ? column.getSortByToggleProps({
                                                    title: '並び順を替える'
                                                }) : ''
                                            )}
                                            width={column.width}
                                            key={'row_' + i + '_' + k}
                                            className={
                                                'cansort_' + column.canSort + '_sorted_' + column.isSorted + ' ' + column?.headerClassName
                                            }
                                        >
                                            {column.render('Header')}
                                            {column.canSort &&
                                                <span className='mg-l-5'>
                                                    {
                                                        column.isSorted ?
                                                            column.isSortedDesc ?
                                                                <i className='fa-solid fa-arrow-down-long'></i>
                                                                :
                                                                <i className='fa-solid fa-arrow-up-long'></i>
                                                            :
                                                            <i className='fa-solid fa-arrow-right-arrow-left fa-rotate-90'></i>
                                                    }
                                                </span>
                                            }
                                        </th>
                                    ))}
                                </tr>
                            ))}
                        </thead>
                    }
                    <tbody {...getTableBodyProps()}>
                        {renderITems.length > 0 &&
                            renderITems.map((row, i) => {
                                prepareRow(row);
                                let customRowClass = '';
                                if (rowClass) customRowClass = rowClass(row);
                                return (
                                    <tr
                                        key={i}
                                        className={
                                            getRowClass(tblCode, row, customRowClass, disableField, disableFieldValue)
                                        }
                                    >
                                        {row.cells.map((cell, k) => {
                                            return (
                                                <td
                                                    width={cell?.column?.width}
                                                    className={cell?.column?.className}
                                                    {...cell.getCellProps()}
                                                    key={i + '_' + k}
                                                >
                                                    {cell.render('Cell')}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                );
                            })}
                    </tbody>
                    <tfoot>
                        {tableFooter && tableFooter}
                    </tfoot>
                </table>
            </div>
            {!hideTableFooter &&
                <div className='table-footer'>
                    {data.length !== 0 ? (
                        !disablePaging && (
                            <>
                                <ReactPaging
                                    forcePage={enableServerSidePaging ? initialTableState.pageIndex : state.pageIndex}
                                    pageCount={Math.ceil(
                                        (enableServerSidePaging ? initialTableState?.totalRecord : rows.length) / state.pageSize
                                    )}
                                    onPageChange={({ selected }) => {
                                        if (enableServerSidePaging) {
                                            state.pageIndex = selected;
                                            onFetch(state);
                                        } else {
                                            gotoPage(selected);
                                            changePageIndexProp(selected);
                                        }
                                    }}
                                />
                                {
                                    showPagingInfo &&
                                    <PagingInfo
                                        tableState={enableServerSidePaging ? initialTableState : state}
                                    />
                                }
                            </>
                        )
                    ) : (
                        <div className='table-no-data w-100'>
                            {!disabledNoData && <p>データはありません</p>}
                        </div>
                    )}
                </div>
            }
        </div>
    );
}

function getRowClass(tblCode, row, customRowClass, disableField, disableFieldValue) {
    if (tblCode === TABLE_CODE.DOCUMENT) {
        const obj = disableField;
        if (obj.documentId != null) {
            if (obj.configFileEdition !== row.original.configFileEdition) {
                return 'disable ' + customRowClass;
            }
            if (row.original.configFileEdition === 'Item') {
                if (obj.formGroupId !== row.original.formGroupId) {
                    return 'disable ' + customRowClass;
                }
            }
            else if (obj.configFileCode !== row.original.configFileCode) {
                return 'disable ' + customRowClass;
            }
        }
    }
    else {
        if (disableFieldValue && row.original[disableField] !== disableFieldValue) {
            return 'disable ' + customRowClass;
        }
        return customRowClass;
    }
    return customRowClass;
}

export default ReactTable;